import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import { fetchBridgeReferralById } from '@/api/modules/bridge'
import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildSetReferralMessage,
  SetReferralFormValues,
} from './referralMessageBuilder'

const setReferralSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  referral: z.object({
    id: z
      .string()
      .min(1, 'Referral ID is required')
      .refine(
        value =>
          !isNaN(Number(value)) &&
          Number.isInteger(Number(value)) &&
          Number(value) >= 0 &&
          Number(value) <= 65535,
        {
          message: 'ID must be a valid 16-bit unsigned integer (0-65535)',
        },
      )
      .refine(
        async referralId => {
          try {
            await fetchBridgeReferralById(referralId)
            return false
          } catch (error) {
            return true
          }
        },
        {
          message: 'Referral ID already exists',
        },
      ),
    withdrawalAddress: z.string().min(1, 'Withdrawal address is required'),
    commissionRate: z
      .string()
      .min(1, 'Commission rate is required')
      .refine(
        value => {
          const num = Number(value)
          return !isNaN(num) && num >= 0 && num <= 1
        },
        {
          message: 'Commission rate must be a valid decimal between 0 and 1',
        },
      ),
  }),
})

export default function SetReferralForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<SetReferralFormValues>({
    resolver: zodResolver(setReferralSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      referral: {
        id: '',
        withdrawalAddress: '',
        commissionRate: '',
      },
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildSetReferralMessage(watchedValues as SetReferralFormValues)
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Set Referral Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to set or update a referral configuration
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            {...form.register('creator')}
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>Referral Details</h4>

          <div className='mt-5 flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='referral.id'>Referral ID</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='referral.id'
                  {...form.register('referral.id')}
                  placeholder='1'
                  disabled={isSubmitting}
                  type='number'
                  min='0'
                  step='1'
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.referral?.id && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.referral.id.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='referral.withdrawalAddress'>
                Withdrawal Address
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='referral.withdrawalAddress'
                  {...form.register('referral.withdrawalAddress')}
                  placeholder='bridge1...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.referral?.withdrawalAddress && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.referral.withdrawalAddress.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='referral.commissionRate'>
                Commission Rate
              </UiLabel>
              <GradientWrapper>
                <UiInput
                  id='referral.commissionRate'
                  {...form.register('referral.commissionRate')}
                  placeholder='0.05'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                />
              </GradientWrapper>
              {form.formState.errors.referral?.commissionRate && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.referral.commissionRate.message}
                </p>
              )}
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'set-referral-message.json')
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildSetReferralMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
